var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { cPointAtOrigin, classNames, splitStringByLineLength } from '@shapeable/utils';
import { useEntity } from '@shapeable/ui';
var cls = classNames('topic-node');
import { lighten } from 'polished';
import { animated, useSpring, to } from '@react-spring/web';
import { useRadar } from '../context/radar-context';
import { get } from 'lodash';
export var SubTopicNodeDefaultProps = {
  circleRadius: 16,
  strokeWidth: 1.5,
  strokeColor: '#FFFFFF',
  index: 0,
  delay: 0,
  labelFontSize: 14
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    ", ";\n\n    ", "\n\n    ", "\n\n    ", "\n  "], ["\n    ", ";\n\n    ", "\n\n    ", "\n\n    ", "\n  "])), theme.TRANSITION('opacity'), function (_a) {
    var isInteractive = _a.isInteractive,
      _color = _a._color;
    return isInteractive && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      cursor: pointer;\n      &:hover {\n        opacity: 1 !important;\n        ", " {\n          fill: ", ";\n        }\n\n        ", " {\n          fill: ", ";\n        }\n\n      }\n\n    "], ["\n      cursor: pointer;\n      &:hover {\n        opacity: 1 !important;\n        ", " {\n          fill: ", ";\n        }\n\n        ", " {\n          fill: ", ";\n        }\n\n      }\n\n    "])), cls.dot('circle'), lighten(0.05, _color), cls.dot('label'), theme.HOVER_COLOR('text', 0.1));
  }, function (_a) {
    var _color = _a._color;
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    "], ["\n    "])));
  }, function (_a) {
    var isActive = _a.isActive;
    return !isActive && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      ", " {\n        filter: none;\n      }\n    "], ["\n      ", " {\n        filter: none;\n      }\n    "])), cls.dot('circle'));
  })
});
var DotStyles = breakpoints({
  base: css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  "], ["\n  "])))
});
var CircleStyles = breakpoints({
  base: css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["  \n    transition: fill 0.2s;\n    filter: drop-shadow( 3px 6px 4px rgba(0, 0, 0, .2));\n\t"], ["  \n    transition: fill 0.2s;\n    filter: drop-shadow( 3px 6px 4px rgba(0, 0, 0, .2));\n\t"])))
});
var OutlineNumberStyles = breakpoints({
  base: css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    user-select: none;\n    \n  "], ["\n    user-select: none;\n    \n  "])))
});
var LabelStyles = breakpoints({
  base: css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n    transition: fill 0.2s;\n    fill: ", "; \n    user-select: none;\n  "], ["\n    transition: fill 0.2s;\n    fill: ", "; \n    user-select: none;\n  "])), theme.COLOR('text'))
});
// -------- Components -------->
var My = {
  Container: styled(animated.svg)(templateObject_9 || (templateObject_9 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Dot: styled.g(templateObject_10 || (templateObject_10 = __makeTemplateObject(["", ""], ["", ""])), DotStyles),
  Circle: styled(animated.circle).attrs(cls.attr('circle'))(templateObject_11 || (templateObject_11 = __makeTemplateObject(["", ""], ["", ""])), CircleStyles),
  OutlineNumber: styled(animated.text).attrs(cls.attr('outline-number'))(templateObject_12 || (templateObject_12 = __makeTemplateObject(["", ""], ["", ""])), OutlineNumberStyles),
  Label: styled(animated.text).attrs(cls.attr('label'))(templateObject_13 || (templateObject_13 = __makeTemplateObject(["", ""], ["", ""])), LabelStyles)
};
export var TopicNode = function (props) {
  var _a, _b;
  var className = props.className,
    children = props.children,
    circleRadius = props.circleRadius,
    onClick = props.onClick,
    radius = props.radius,
    radiusStart = props.radiusStart,
    labelFontSize = props.labelFontSize,
    strokeColor = props.strokeColor,
    strokeWidth = props.strokeWidth,
    animate = props.animate,
    isActive = props.isActive,
    angle = props.angle,
    originX = props.originX,
    originY = props.originY,
    angleStart = props.angleStart,
    springConfig = props.springConfig;
  var entity = useEntity(props.entity);
  var color = ((_a = entity === null || entity === void 0 ? void 0 : entity.color) === null || _a === void 0 ? void 0 : _a.value) || '#000000';
  var radar = useRadar();
  var isLeftHalf = angle > 90 && angle < 270;
  var isBottomHalf = angle < 180;
  var defn = (_b = radar.topicNodes) === null || _b === void 0 ? void 0 : _b[entity.slug];
  var textAnchor = (defn === null || defn === void 0 ? void 0 : defn.textAnchor) || (isLeftHalf ? 'end' : 'start');
  var labelXDelta = (defn === null || defn === void 0 ? void 0 : defn.offsetX) !== undefined ? defn === null || defn === void 0 ? void 0 : defn.offsetX : isLeftHalf ? -25 : 25;
  var labelYDelta = (defn === null || defn === void 0 ? void 0 : defn.offsetY) || 0;
  var isTopHalf = false;
  var cPointOrigin = cPointAtOrigin(originX, originY);
  // SVG container redefines the origin, so these should be relative to 0
  var cPoint = cPointAtOrigin(0, 0);
  var label = entity.label || entity.name;
  var lines = splitStringByLineLength(label, 14);
  var labelPoint = cPoint(0, angle);
  var hasStartingValues = radiusStart !== undefined && angleStart !== undefined;
  var duration = get(springConfig, 'duration', 600);
  var isAnimated = animate && hasStartingValues && !!duration;
  var finalOpacity = isActive ? 1 : 0.4;
  var toValues = {
    // cx,
    // cy,
    opacity: finalOpacity,
    radius: radius,
    angle: angle
  };
  var values = isAnimated ? useSpring({
    from: {
      // cxStart,
      // cyStart,
      opacity: 0,
      radius: radiusStart,
      angle: angleStart
    },
    to: toValues,
    config: springConfig,
    delay: props.delay + props.index * 1
  }) : toValues;
  var xValue = function (angle, radius) {
    return cPointOrigin(radius, angle).cx;
  };
  var yValue = function (angle, radius) {
    return cPointOrigin(radius, angle).cy;
  };
  var fontSize = circleRadius * 0.9;
  var fontScaleFactor = 14 / 10;
  var handleClick = function () {
    if (onClick) {
      onClick(entity);
    }
  };
  return React.createElement(My.Container, {
    isActive: isActive,
    isInteractive: !!onClick,
    onClick: handleClick,
    style: {
      opacity: values.opacity
    },
    _color: color,
    overflow: "visible",
    x: to([values.angle, values.radius], xValue),
    y: to([values.angle, values.radius], yValue),
    className: cls.name(className)
  }, React.createElement("g", null, React.createElement(My.Dot, null, React.createElement(My.Circle, {
    r: circleRadius,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    fill: color
  }), React.createElement(My.OutlineNumber, {
    fontFamily: 'Montserrat',
    x: 0,
    y: (circleRadius * 2 - fontSize * fontScaleFactor) / 2,
    textAnchor: "middle",
    fontWeight: 400,
    fill: strokeColor,
    fontSize: fontSize
  }, entity === null || entity === void 0 ? void 0 : entity.outlineNumber)), React.createElement(My.Label, {
    fontFamily: 'Montserrat',
    fontSize: labelFontSize,
    fontWeight: 400,
    textAnchor: textAnchor,
    x: labelPoint.cx + labelXDelta,
    y: labelPoint.cy + labelYDelta + (isTopHalf && lines.length === 1 ? 12.5 : 0)
  }, lines.map(function (word, i) {
    return React.createElement("tspan", {
      key: "".concat(entity.id, "-word-").concat(i),
      dy: "".concat(i == 0 ? 0 : 1.2, "em"),
      x: labelPoint.cx + labelXDelta
    }, word);
  }))), children);
};
TopicNode.defaultProps = SubTopicNodeDefaultProps;
TopicNode.cls = cls;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13;